/**
 * Gedeelde verlof-weergave-regels voor de planning (FO-021 + FO-025).
 *
 * Deze module is de ENE bron van waarheid voor twee dingen die voorheen op
 * minstens vier plekken los waren uitgeschreven (planbord-query, capaciteits-
 * engine, conflict-detectie, portal-planning, twee verlof-inboxen):
 *
 *   1. "Is dit verlof actief?" - welke verlof-statussen tellen als vast
 *      (goedgekeurd/definitief), welke als nog-lopende aanvraag (niet-groen),
 *      en welke helemaal niet als verlof-blok in de planning verschijnen
 *      (afgekeurd/overruled/ingetrokken/concept).
 *   2. De verlof-tijd-formatter - "Hele dag" / "van - tot" / "Dagdeel (X)".
 *
 * Eigendom: de planning-verlof-stream bezit deze regels; andere streams
 * (uren-weekstaat) consumeren ze read-only. Wijzig de buckets hier, niet in een
 * losse kopie elders.
 */

import { formatHoursHuman } from './werkbon-hours'
import { formatNlDatumKort } from './datum-weergave'
import { parseStructureelPatroon } from './verlof-structureel-patroon'

// ─── "Is dit verlof actief?"-regel ──────────────────────────────────────────
//
// Drie disjuncte buckets over de LeaveStatus-keten (V1 legacy + V2 9-stap-flow):
//
//   actief      → vast verlof: GROEN, blokkeert werkbon-toewijzing (conflict),
//                 reserveert capaciteit. = exact de conflict-detectie-set.
//   aangevraagd → nog lopende aanvraag: NIET-groen "aangevraagd"-stijl. Zichtbaar
//                 zodat de planner weet dat er iets loopt, maar telt niet als
//                 vast verlof.
//   geen        → afgekeurd (alle afkeur-statussen), overruled, ingetrokken en
//                 concept: GEEN verlof-blok in enige planning-weergave en geen
//                 capaciteits-aftrek (de dag is weer normaal beschikbaar).
//
// `as const` houdt de string-literals smal zodat de arrays toewijsbaar zijn aan
// een Prisma `LeaveStatus[]` zonder cast (capacity-engine, le-routes).

/** Vast verlof (groen): V1 GOEDGEKEURD of V2 DEFINITIEF. Spiegelt de conflict-detectie. */
export const VERLOF_ACTIEF_STATUSSEN = ['GOEDGEKEURD', 'DEFINITIEF'] as const

/**
 * Nog-lopende aanvraag (niet-groen "aangevraagd"). Bevat de V1-pending
 * (AANGEVRAAGD) en de V2-pending-fases. AANVULLENDE_VRAAG hoort hier ook: de
 * aanvraag staat tijdelijk bij de medewerker (TL stelde een vraag) maar leeft
 * nog en keert terug naar INGEDIEND - dus zichtbaar als "aangevraagd", niet
 * verborgen.
 */
export const VERLOF_AANGEVRAAGD_STATUSSEN = [
  'AANGEVRAAGD',
  'INGEDIEND',
  'GOEDGEKEURD_TEAMLEIDER',
  'GECLASSIFICEERD',
  'AANVULLENDE_VRAAG',
] as const

/**
 * Alle statussen die als verlof-blok in de planning getoond worden (actief +
 * aangevraagd). Gebruik dit in de weergave-queries (planbord + portal-planning);
 * afgekeurd/overruled/ingetrokken/concept zitten er bewust NIET in.
 */
export const VERLOF_WEERGAVE_STATUSSEN = [
  ...VERLOF_ACTIEF_STATUSSEN,
  ...VERLOF_AANGEVRAAGD_STATUSSEN,
] as const

export type VerlofWeergaveBucket = 'actief' | 'aangevraagd' | 'geen'

const ACTIEF_SET: ReadonlySet<string> = new Set(VERLOF_ACTIEF_STATUSSEN)
const AANGEVRAAGD_SET: ReadonlySet<string> = new Set(VERLOF_AANGEVRAAGD_STATUSSEN)

/**
 * Telt dit verlof als vast/actief (groen)? = goedgekeurd/definitief. Eén regel
 * voor weergave, conflict-detectie en capaciteit zodat ze nooit meer uiteenlopen
 * (FO-021).
 */
export function isVerlofActief(status: string): boolean {
  return ACTIEF_SET.has(status)
}

/** Classificeer een verlof-status in de drie weergave-buckets (FO-021). */
export function verlofWeergaveBucket(status: string): VerlofWeergaveBucket {
  if (ACTIEF_SET.has(status)) return 'actief'
  if (AANGEVRAAGD_SET.has(status)) return 'aangevraagd'
  return 'geen'
}

/** Hoort dit verlof als blok in de planning te verschijnen? (actief of aangevraagd) */
export function isVerlofZichtbaarInPlanning(status: string): boolean {
  return verlofWeergaveBucket(status) !== 'geen'
}

// ─── batch-57 (FO-418) — "geblokkeerd (roostervrij)"-dagen ──────────────────
//
// Een verlofdag die volgens het rooster 0 uur kost (weekend, 0-urendag) is
// vastgelegd als BLOKKADE: geen saldo-afboeking, geen ledger-mutatie, wel
// zichtbaar als afwezigheid. Op het planbord krijgt zo'n dag een eigen band
// (eigen label + kleur) i.p.v. de gewone verlof-band, zodat een planner meteen
// ziet dat er geen verlofuren tegenover staan maar de medewerker die dag wel
// bewust niet oproepbaar is.
//
// De dag-set (`blokkadeDagen`) wordt server-side bepaald uit de roosteruren per
// kalenderdag; de helpers hier zijn puur en zijn de ENIGE plek waar het label,
// de korte variant en de dag-toets staan (planbord desktop + mobiel + het
// consignatie-waarschuwingspad lezen ze alle drie hier).

/** Volledig planbord-/weergavelabel voor een 0-uurs verlofdag. */
export const VERLOF_BLOKKADE_LABEL = 'Geblokkeerd (roostervrij)'

/** Korte variant voor smalle cellen (compacte banner, mobiel planbord). */
export const VERLOF_BLOKKADE_LABEL_KORT = 'Geblokkeerd'

/** Vaste toelichting bij een aanvraag zonder verlofuren (portal + backoffice). */
export const VERLOF_BLOKKADE_TOELICHTING = 'Deze aanvraag kost geen verlofuren'

/** Compacte variant voor onder een planbord-band ("Kost geen verlofuren"). */
export const VERLOF_BLOKKADE_SUBTEKST = 'Kost geen verlofuren'

/** Minimale vorm die de dag-toets nodig heeft (planbord-blok of DTO). */
export interface VerlofBlokkadeDagenBron {
  /** yyyy-mm-dd-dagen binnen de aanvraag die 0 roosteruren kosten. */
  blokkadeDagen?: string[] | null
}

/**
 * Is deze kalenderdag (yyyy-mm-dd) binnen de aanvraag een roostervrije blokkade?
 *
 * `blokkadeDagen` ontbreekt bij aanvragen met handmatig opgegeven uren
 * (ploegendienst-weekend dat het rooster niet kent) - die kosten wél uren en
 * zijn dus geen blokkade.
 */
export function isVerlofBlokkadeDag(blok: VerlofBlokkadeDagenBron, iso: string): boolean {
  const dagen = blok.blokkadeDagen
  if (!dagen || dagen.length === 0) return false
  return dagen.includes(iso.slice(0, 10))
}

/**
 * Toont deze kalenderdag de eigen band "Geblokkeerd (roostervrij)"?
 *
 * Twee voorwaarden, en de tweede is makkelijk te vergeten: de dag moet
 * roostervrij zijn ÉN het verlof moet vaststaan. Een nog te beoordelen 0-uurs
 * aanvraag mag niet als dichte dag lezen - de teamleider kan hem nog afwijzen -
 * en houdt dus de gewone "aangevraagd"-stijl. Eén regel, gedeeld door het
 * desktop- en het mobiele planbord.
 */
export function toontVerlofBlokkadeBand(
  status: string,
  blok: VerlofBlokkadeDagenBron,
  iso: string,
): boolean {
  return isVerlofActief(status) && isVerlofBlokkadeDag(blok, iso)
}

/**
 * Bestaat de aanvraag UITSLUITEND uit roostervrije (0-uurs) dagen? Dat is de
 * voorwaarde voor de optionele auto-goedkeuring (VerlofBeleid
 * `autoGoedkeuringNulUren`): een gemengde periode (vr t/m ma) kost wél uren en
 * blijft de gewone TL-/HR-keten volgen.
 *
 * `dagen` = alle kalenderdagen van de aanvraag met hun roosteruren.
 */
export function isUitsluitendNulUren(
  dagen: ReadonlyArray<{ roosterUren: number }>,
  totaalUren: number,
): boolean {
  if (dagen.length === 0) return false
  if (totaalUren > 0) return false
  return dagen.every((d) => d.roosterUren === 0)
}

// ─── Verlof-tijd-formatter ──────────────────────────────────────────────────

export interface VerlofTijdInput {
  /** `true` = deel-dag (geen hele dag). Hele dag → "Hele dag". */
  isPartialDay?: boolean | null
  /** "HH:mm" begintijd bij een deel-dag met tijdvak (Europe/Amsterdam wandklok). */
  startTijd?: string | null
  /** "HH:mm" eindtijd bij een deel-dag met tijdvak. */
  eindTijd?: string | null
  /** Aantal verlof-uren - voor de "Dagdeel (X)"-fallback en de optionele duur-suffix. */
  hours?: number | null
}

/**
 * Gedeelde verlof-tijd-formatter (FO-025). Toont WANNEER iemand vrij is, naast
 * de duur die de consument er los bij zet (bv. een aparte uren-kolom):
 *
 *   - hele dag           → "Hele dag"
 *   - dagdeel + tijdvak   → "09:00 - 12:00"
 *   - dagdeel zonder tijd → "Dagdeel (45min)"  (fallback met de uren)
 *   - dagdeel, geen uren  → "Dagdeel"
 *
 * Meerdaags verlof is doorgaans hele dagen → "Hele dag" (de dag(en) staan los).
 * Met `metDuur: true` wordt de duur inline tussen haakjes meegegeven bij een
 * tijdvak ("09:00 - 12:00 (45min)") - handig voor de compacte planning-blokken
 * die geen aparte uren-kolom hebben. De inboxes laten `metDuur` weg en tonen de
 * uren in hun eigen kolom.
 *
 * Tijden zijn al "HH:mm" wandklok uit de DB; we koppelen met " - " (geen
 * em-/en-dash) conform de STREEP-copy-regels.
 */
export function formatVerlofTijd(v: VerlofTijdInput, opts?: { metDuur?: boolean }): string {
  if (!v.isPartialDay) return 'Hele dag'
  const duur = v.hours != null && v.hours > 0 ? formatHoursHuman(v.hours) : null
  if (v.startTijd && v.eindTijd) {
    const tijdvak = `${v.startTijd} - ${v.eindTijd}`
    return opts?.metDuur && duur ? `${tijdvak} (${duur})` : tijdvak
  }
  if (duur) return `Dagdeel (${duur})`
  return 'Dagdeel'
}

// ─── Verlof-periode-label (FO-309 P1) ────────────────────────────────────────
//
// Eén gedeelde bron voor het complete verlof-vak-label: het datum(bereik) PLUS
// de tijd/omvang ("Hele dag" / "09:00 - 13:00"), zodat de tijden overal ín het
// verlofvak staan i.p.v. alleen in de paginakop. Voorheen schreef ~10 schermen
// (portal-lijst/-detail/-dashboard, backoffice goedkeuren/overzicht/dossier/
// kalender) hun eigen `fmtDate(...) t/m ...`- en tijdvak-logica los uit.
//
// De React-wrapper `VerlofPeriodeLabel` (`@streep/ui`) rendert deze parts; de
// pure functie hier is testbaar (snapshot-fixtures) en tz-vast.

/** ISO-weekdag (1 = maandag … 7 = zondag) → NL-naam, voor het patroon-label. */
const ISO_WEEKDAG_NAMEN = [
  '',
  'maandag',
  'dinsdag',
  'woensdag',
  'donderdag',
  'vrijdag',
  'zaterdag',
  'zondag',
] as const

export interface VerlofPeriodeInput extends VerlofTijdInput {
  /** Startdatum (ISO-string of Date; UTC-middernacht van de NL-kalenderdag). */
  startDate: string | Date
  /** Einddatum; `null`/gelijk aan start = één dag. */
  endDate?: string | Date | null
  /** Structureel weekpatroon (ouderschap) uit `LeaveRequest.structureelPatroon`. */
  structureelPatroon?: unknown
}

export interface VerlofPeriodeParts {
  /** Datum(bereik): "24 jun. 2026" of "22 jun. 2026 t/m 26 jun. 2026". */
  datum: string
  /** Tijd/omvang: "Hele dag" | "09:00 - 13:00" | "Dagdeel (45min)". */
  tijd: string
  /** Compacte patroon-omschrijving (ouderschap), of `null`. */
  patroon: string | null
  /** Beslaat de aanvraag meer dan één kalenderdag? */
  meerdaags: boolean
  /**
   * Volledig één-regel-label voor tooltips/plain-text (kalendercel zonder ruimte):
   * "22 jun. 2026 t/m 26 jun. 2026 · hele dag" of "24 jun. 2026 · 09:00 - 13:00".
   */
  volledig: string
}

/** yyyy-mm-dd van een ISO-string/Date (UTC-kalenderdag; matcht de opslag-conventie). */
function dagKey(d: string | Date): string {
  if (typeof d === 'string') return d.slice(0, 10)
  return d.toISOString().slice(0, 10)
}

/**
 * Bouw het gedeelde verlof-periode-label (FO-309 P1). Combineert het
 * datum(bereik) met de tijd/omvang zodat de tijd altijd ín het verlofvak staat.
 *
 *   - hele dag, één dag        → datum "24 jun. 2026" · "Hele dag"
 *   - dagdeel met tijdvak      → datum · "09:00 - 13:00"
 *   - meerdaags (hele dagen)   → "22 jun. 2026 t/m 26 jun. 2026" · "Hele dag"
 *   - structureel patroon      → datum-bereik · patroon "wekelijks op woensdag"
 *
 * Meerdaags verlof met per-dag afwijkende tijden bestaat niet in het datamodel
 * (een dagdeel-tijdvak vereist start==eind, submit-refine leave-v2); het
 * "patroon"-geval is uitsluitend het structurele ouderschaps-weekpatroon.
 */
export function verlofPeriodeParts(
  input: VerlofPeriodeInput,
  opts?: { metDuur?: boolean },
): VerlofPeriodeParts {
  const startKey = dagKey(input.startDate)
  const eindKey = input.endDate != null ? dagKey(input.endDate) : startKey
  const meerdaags = eindKey !== startKey

  const startLabel = formatNlDatumKort(startKey)
  const datum = meerdaags ? `${startLabel} t/m ${formatNlDatumKort(eindKey)}` : startLabel

  const patroonJson = parseStructureelPatroon(input.structureelPatroon)
  let patroon: string | null = null
  let tijd: string
  if (patroonJson) {
    const weekdagNaam = ISO_WEEKDAG_NAMEN[patroonJson.weekdag] ?? ''
    patroon = weekdagNaam ? `wekelijks op ${weekdagNaam}` : 'wekelijks patroon'
    // Een patroon met urenPerKeer is een dagdeel per keer; anders hele dagen.
    tijd =
      patroonJson.urenPerKeer != null && patroonJson.urenPerKeer > 0
        ? `Dagdeel (${formatHoursHuman(patroonJson.urenPerKeer)})`
        : 'Hele dag'
  } else {
    tijd = formatVerlofTijd(input, opts)
  }

  const staart = patroon ? `${patroon} · ${tijd.toLowerCase()}` : tijd.toLowerCase()
  const volledig = `${datum} · ${staart}`
  return { datum, tijd, patroon, meerdaags, volledig }
}
